import type { Dispatch, FC, SetStateAction } from "react";

import type { ArticleDataPartial } from "../../ArticleRestrictedContent/ArticleRestrictedContent";
import { QuizCompleteScreen } from "../QuizCompleteScreen";
import { QuizExitConfirmation } from "../QuizExitConfirmation";
import { QuizModal } from "../QuizModal";
import { QuizQuestion } from "../QuizQuestion";
import type { QuizContentConfig } from "../types";

import { useQuizContent } from "./useQuizContent";

export type QuizContentProps = {
  isOpened: boolean;
  onClose: () => void;
  setArticleData: Dispatch<SetStateAction<ArticleDataPartial>>;
  config: QuizContentConfig;
  articleId?: string;
  points: number | null;
};

export const QuizContent: FC<QuizContentProps> = ({ isOpened, onClose, setArticleData, config, articleId, points }) => {
  const {
    currentQuestion,
    currentQuestionIndex,
    selectedAnswer,
    answerState,
    isCompleted,
    isSubmitting,
    isShowingExitConfirmation,
    handleAnswerSelect,
    handleNext,
    handleCloseRequest,
    handleDropQuiz,
    handleStay,
  } = useQuizContent({ config, articleId, points, isOpened, onClose, setArticleData });

  const { completeScreen, exitConfirmation, questions } = config;

  const renderContent = () => {
    if (isShowingExitConfirmation) {
      return (
        <QuizExitConfirmation
          title={exitConfirmation.title}
          description={exitConfirmation.description}
          dropQuizLabel={exitConfirmation.dropQuizLabel}
          stayLabel={exitConfirmation.stayLabel}
          onDropQuiz={handleDropQuiz}
          onStay={handleStay}
        />
      );
    }

    if (isCompleted) {
      return (
        <QuizCompleteScreen
          title={completeScreen.title}
          description={completeScreen.description}
          points={points ?? 0}
          pointsLabel={completeScreen.pointsLabel}
          defaultCtaLabel={completeScreen.defaultCtaLabel}
          onDefaultCtaClick={onClose}
          ctas={completeScreen.ctas?.map((cta) => ({ label: cta.label, href: cta.url }))}
        />
      );
    }

    return (
      <QuizQuestion
        questionNumber={currentQuestionIndex + 1}
        totalQuestions={questions.length}
        title={currentQuestion.title}
        description={currentQuestion.description}
        options={currentQuestion.options.map((opt, i) => ({ value: String(i), label: opt.text }))}
        selectedValue={selectedAnswer}
        onChange={handleAnswerSelect}
        answerState={answerState}
        correctFeedbackText={currentQuestion.correctFeedback}
        incorrectFeedbackText={currentQuestion.incorrectFeedback}
        onNext={handleNext}
        isSubmitting={isSubmitting}
        nextButtonLabel={currentQuestion.nextButtonLabel}
        progressTemplate={config.progressTemplate}
      />
    );
  };

  return (
    <QuizModal
      isOpened={isOpened}
      onClose={handleCloseRequest}
      accessibilityDialogLabel={config.accessibilityDialogLabel}
    >
      {renderContent()}
    </QuizModal>
  );
};
